// Vendor Program v2 PR 2 — complaint detail drawer (deep-linkable via
// ?complaint=<id> on the queue page). Detail in the drawer; actions are
// modals: Acknowledge (direct), Resolve (required reason), Hand off to
// compliance intake (optional suggested severity). Triage actions gated
// Vendors.Manage / ADMIN.

import { useState } from "react";
import { Link } from "react-router-dom";
import { Button, StatusBadge } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { extractErrorMessage } from "@/api/client";
import { ResourceDrawer } from "@/components/portfolioAdmin/loanDetail/common/ResourceDrawer";
import { useDateFormatter } from "@/utils/datetime";
import {
  useAcknowledgeComplaint,
  useHandOffComplaint,
  useResolveComplaint,
  useVendorComplaint,
} from "@/hooks/portfolioAdmin/useVendorReports";
import {
  COMPLAINT_STATUS_LABEL,
  complaintStatusTone,
} from "../reports/vendorReportFormat";

// LoanFailure's triage vocabulary — a signal for the hand-off, never the
// formal ComplianceSeverity (which B4 assigns at conversion).
const SEVERITY_OPTIONS = ["Minor", "Major", "Critical"] as const;

type ModalMode = null | "resolve" | "handoff";

interface VendorComplaintDrawerProps {
  complaintId: string;
  open: boolean;
  onClose: () => void;
}

export function VendorComplaintDrawer({
  complaintId,
  open,
  onClose,
}: VendorComplaintDrawerProps) {
  const toast = useToast();
  const auth = useAuth();
  const { date } = useDateFormatter();
  const complaintQuery = useVendorComplaint(complaintId);
  const complaint = complaintQuery.data;

  const acknowledgeMutation = useAcknowledgeComplaint(complaintId);
  const resolveMutation = useResolveComplaint(complaintId);
  const handoffMutation = useHandOffComplaint(complaintId);

  const [modal, setModal] = useState<ModalMode>(null);
  const [reason, setReason] = useState("");
  const [severity, setSeverity] = useState<string>("");

  const canManage =
    auth.hasPermission("Vendors.Manage") || auth.hasRoleCode("ADMIN");
  const isOpenStatus =
    complaint?.status === "new" || complaint?.status === "acknowledged";

  const onAcknowledge = async () => {
    try {
      await acknowledgeMutation.mutateAsync();
      toast.success("Complaint acknowledged.");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const onResolve = async () => {
    try {
      await resolveMutation.mutateAsync({ reason: reason.trim() });
      toast.success("Complaint resolved.");
      setModal(null);
      setReason("");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const onHandOff = async () => {
    try {
      await handoffMutation.mutateAsync({
        suggestedSeverity: severity || null,
      });
      toast.success("Complaint handed off to compliance intake.");
      setModal(null);
      setSeverity("");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      title="Complaint"
      subtitle={
        complaint
          ? `${complaint.vendorName} · ${complaint.loanNumber}`
          : undefined
      }
      testId="vendor-complaint-drawer"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Close</Button>
          {canManage && complaint && isOpenStatus && (
            <>
              {complaint.status === "new" && (
                <Button
                  variant="secondary"
                  onClick={() => void onAcknowledge()}
                  disabled={acknowledgeMutation.isPending}
                  data-testid="complaint-acknowledge-button"
                >
                  Acknowledge
                </Button>
              )}
              <Button
                variant="secondary"
                onClick={() => setModal("handoff")}
                data-testid="complaint-handoff-button"
              >
                Hand off to compliance
              </Button>
              <Button
                variant="primary"
                onClick={() => setModal("resolve")}
                data-testid="complaint-resolve-button"
              >
                Resolve
              </Button>
            </>
          )}
        </>
      }
    >
      {complaintQuery.isLoading && (
        <p className="text-small text-gray-mid italic m-0">Loading complaint…</p>
      )}
      {complaintQuery.isError && (
        <p className="text-small text-status-error m-0">
          Could not load the complaint. Refresh to retry.
        </p>
      )}
      {complaint && (
        <div className="flex flex-col gap-space-4">
          <section>
            <div className="flex items-center gap-space-2 mb-space-2">
              <StatusBadge tone={complaintStatusTone(complaint.status)}>
                {COMPLAINT_STATUS_LABEL[complaint.status]}
              </StatusBadge>
              <span className="text-caption text-gray-mid">
                Received {date(complaint.createdAt)}
              </span>
            </div>
            <p
              className="text-small m-0 whitespace-pre-wrap rounded-md bg-white-warm p-space-3"
              data-testid="complaint-text"
            >
              {complaint.text}
            </p>
          </section>

          <section>
            <h3 className="font-display font-semibold text-h4 m-0 mb-space-2">
              Context
            </h3>
            <ul className="m-0 p-0 list-none flex flex-col gap-space-1 text-small">
              <li>
                Loan{" "}
                <Link
                  to={`/portfolio-admin/loans/${complaint.loanId}`}
                  className="text-gold-shadow hover:underline"
                  data-testid="complaint-loan-link"
                >
                  {complaint.loanNumber}
                </Link>
              </li>
              <li>
                Vendor{" "}
                <Link
                  to={`/portfolio-admin/vendors/${complaint.vendorId}`}
                  className="text-gold-shadow hover:underline"
                  data-testid="complaint-vendor-link"
                >
                  {complaint.vendorName}
                </Link>
              </li>
              {complaint.status === "handed-off" &&
                complaint.complianceIntakeReportId && (
                  <li>
                    <Link
                      to={`/portfolio-admin/loans/${complaint.loanId}?tab=legal-review&report=${complaint.complianceIntakeReportId}`}
                      className="text-gold-shadow hover:underline"
                      data-testid="complaint-intake-link"
                    >
                      View compliance intake report
                    </Link>
                  </li>
                )}
            </ul>
          </section>

          <section>
            <h3 className="font-display font-semibold text-h4 m-0 mb-space-2">
              History
            </h3>
            <ul
              className="m-0 p-0 list-none flex flex-col gap-space-1 text-small"
              data-testid="complaint-history"
            >
              <li>Created {date(complaint.createdAt)}</li>
              {complaint.acknowledgedAt && (
                <li>Acknowledged {date(complaint.acknowledgedAt)}</li>
              )}
              {complaint.resolvedAt && (
                <li>
                  Resolved {date(complaint.resolvedAt)}
                  {complaint.resolvedReason
                    ? ` — ${complaint.resolvedReason}`
                    : ""}
                </li>
              )}
            </ul>
          </section>
        </div>
      )}

      {modal === "resolve" && (
        <Modal
          open
          onClose={() => setModal(null)}
          title="Resolve complaint"
          description="Record how the complaint was addressed. The reason is kept on the record."
          size="sm"
          footer={
            <>
              <Button variant="secondary" onClick={() => setModal(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => void onResolve()}
                disabled={!reason.trim() || resolveMutation.isPending}
                data-testid="complaint-resolve-confirm"
              >
                Resolve complaint
              </Button>
            </>
          }
        >
          <label className="flex flex-col gap-space-1 text-caption">
            <span className="text-gray-mid uppercase tracking-label">
              Reason (required)
            </span>
            <textarea
              className="min-h-[3.5rem] px-space-2 py-space-1 border border-border-light rounded-md text-small"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="complaint-resolve-reason"
            />
          </label>
        </Modal>
      )}

      {modal === "handoff" && (
        <Modal
          open
          onClose={() => setModal(null)}
          title="Hand off to compliance"
          description="Creates a compliance intake report on the loan for B4 triage. Conversion to a formal exception stays a manual compliance step."
          size="sm"
          footer={
            <>
              <Button variant="secondary" onClick={() => setModal(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => void onHandOff()}
                disabled={handoffMutation.isPending}
                data-testid="complaint-handoff-confirm"
              >
                Hand off
              </Button>
            </>
          }
        >
          <label className="flex flex-col gap-space-1 text-caption">
            <span className="text-gray-mid uppercase tracking-label">
              Suggested severity (optional)
            </span>
            <select
              className="h-9 px-space-2 border border-border-light rounded-md text-small bg-white-pure"
              value={severity}
              onChange={(e) => setSeverity(e.target.value)}
              data-testid="complaint-handoff-severity"
            >
              <option value="">No suggestion</option>
              {SEVERITY_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>
        </Modal>
      )}
    </ResourceDrawer>
  );
}
